import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  type ConnectorPlatform,
  type PlatformCategory,
  CONNECTOR_PLATFORM_CATEGORY,
  CONNECTOR_PLATFORM_LABELS,
  listPlatformsQueryOptions,
  PLATFORM_CATEGORY_ORDER,
} from '@/entities/media-connector';
import {
  IQ_DATA_SOURCE_LABELS,
  IQ_DATA_SOURCE_ORDER,
  iqDataSourcesQueryOptions,
} from '@/entities/iq-data-source';
import { usePermissions } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import {
  STATISTA_PLACEHOLDER_COPY,
  STATISTA_PLACEHOLDER_ID,
} from '../StatistaPlaceholderPane/statistaPlaceholder';
import type {
  CategoryFilter,
  UseMediaConnectorsWorkspaceReturn,
} from './useMediaConnectorsWorkspace.types';

// Platforms grouped by category, derived from the canonical category map so a newly
// tagged platform lands in the right group automatically — no parallel list to sync.
const PLATFORMS_BY_CATEGORY = PLATFORM_CATEGORY_ORDER.reduce(
  (acc, category) => {
    acc[category] = (Object.keys(CONNECTOR_PLATFORM_CATEGORY) as ConnectorPlatform[]).filter(
      (platform) => CONNECTOR_PLATFORM_CATEGORY[platform] === category,
    );
    return acc;
  },
  {} as Record<PlatformCategory, ConnectorPlatform[]>,
);

/**
 * Rail data, category filter and search state behind the Paid Media & Ad
 * Intelligence `NavigationPanel`. Instantiated once so filter state is
 * shared between the panel's filter slot and the item list it narrows.
 */
export function useMediaConnectorsWorkspace(): UseMediaConnectorsWorkspaceReturn {
  const { platform: platformParam } = useParams<{ platform: string }>();
  const { data: platformsData, isPending: isLoadingPlatforms } = useQuery(
    listPlatformsQueryOptions(),
  );
  // Same contract as the platform list: the backend withholds a source the caller has no
  // licence for, so an unlicensed one is simply absent and never reaches the rail.
  const { data: dataSources } = useQuery(iqDataSourcesQueryOptions());
  // Component state, so it survives navigation within the media subtree: the index and detail
  // routes render the same `<MediaConnectors />` element as siblings at equal depth, so React
  // reconciles one instance across the hop rather than remounting. It resets on a reload or on
  // leaving the subtree; lifting it into a query param is what would make it shareable too, and
  // is deliberately not done here.
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const { canAccessStatistaIntegration } = usePermissions();
  const isStatistaFlagOn = useBooleanFlagValue('statista_placeholder', false);
  // Both legs, as every sibling research integration does. The permission is granted in the
  // external Platform, so the flag is the only switch an engineer can throw here. Deliberately
  // not `statista_data_provider`: that one authorises credit spend against a shared licence, so
  // reusing it would mean buying real Statista access to show a card that does nothing.
  const showStatista = isStatistaFlagOn && canAccessStatistaIntegration;
  // Held above the pane, which unmounts when the route leaves Statista.
  const [isStatistaConnected, setIsStatistaConnected] = useState(false);

  // Platforms the workspace is allowed to see. The backend already filters this by
  // each platform's per-platform feature flag, so a disabled platform is simply
  // absent here and never renders — the frontend does not check flags itself.
  const enabledPlatforms = useMemo(
    () => new Set((platformsData?.items ?? []).map((p) => p.platform)),
    [platformsData],
  );

  const categoryFilters = useMemo<ReadonlyArray<CategoryFilter>>(
    () => [
      'all',
      ...PLATFORM_CATEGORY_ORDER.filter((category) =>
        PLATFORMS_BY_CATEGORY[category].some((platform) => enabledPlatforms.has(platform)),
      ),
    ],
    [enabledPlatforms],
  );

  /**
   * Ordered by the entity's own list rather than the response, so an entry keeps its rail
   * position as its activation state changes. Hidden under a category filter: these belong
   * to none of the paid-media categories, and inventing one for two entries would read as a
   * platform group they are not.
   */
  const dataSourceItems = useMemo(() => {
    if (categoryFilter !== 'all') return [];
    const available = new Set((dataSources ?? []).map((item) => item.source));
    return IQ_DATA_SOURCE_ORDER.filter((source) => available.has(source)).map((source) => ({
      id: source,
      label: IQ_DATA_SOURCE_LABELS[source],
    }));
  }, [categoryFilter, dataSources]);

  const items = useMemo(
    () => [
      ...PLATFORM_CATEGORY_ORDER.filter(
        (category) => categoryFilter === 'all' || categoryFilter === category,
      ).flatMap((category) =>
        PLATFORMS_BY_CATEGORY[category]
          .filter((platform) => enabledPlatforms.has(platform))
          .map((platform) => ({ id: platform, label: CONNECTOR_PLATFORM_LABELS[platform] })),
      ),
      ...dataSourceItems,
      // Hidden under a category filter for the same reason the data sources are: it belongs
      // to none of the paid-media categories.
      ...(categoryFilter === 'all' && showStatista
        ? [{ id: STATISTA_PLACEHOLDER_ID, label: STATISTA_PLACEHOLDER_COPY.label }]
        : []),
    ],
    [categoryFilter, enabledPlatforms, dataSourceItems, showStatista],
  );

  return {
    items,
    isLoadingPlatforms,
    activePlatform: platformParam ?? null,
    categoryFilter,
    setCategoryFilter,
    categoryFilters,
    hrefFor: (platform) => ROUTES.KNOWLEDGE_HUB_MEDIA_DETAIL(platform),
    // Gated off, the id falls through to `MediaConnectorPane`'s "unavailable" — the same
    // non-answer an unlicensed caller gets for a data source, so the gate leaks nothing.
    showStatistaPane: showStatista && platformParam === STATISTA_PLACEHOLDER_ID,
    isStatistaConnected,
    setIsStatistaConnected,
  };
}
